<template>
	<div class="opportunities-kanban-wrapper">
		<!-- Toolbar -->
		<v-toolbar flat dense class="background-1">
			<v-icon class="mr-2">mdi-view-column</v-icon>
			<span class="font-weight-medium subtitle-1">Pipeline {{ $trans('opportunities') }}</span>

			<v-spacer></v-spacer>

			<!-- Period Filter -->
			<v-select
				v-model="filters.period"
				:items="periodOptions"
				:menu-props="{'offset-y': true}"
				label="Periodo"
				dense
				outlined
				hide-details
				class="mr-2"
				style="max-width: 160px;"
				@change="loadKanban"
			></v-select>

			<!-- Division Filter -->
			<v-select
				v-model="filters.division_id"
				:items="divisions"
				:menu-props="{'offset-y': true}"
				item-text="text"
				item-value="value"
				label="Divisione"
				dense
				outlined
				hide-details
				clearable
				class="mr-2"
				style="max-width: 200px;"
				@change="loadKanban"
			></v-select>

			<!-- Owner Filter -->
			<v-select
				v-model="filters.owner_id"
				:items="owners"
				:menu-props="{'offset-y': true}"
				item-text="text"
				item-value="value"
				label="Responsabile"
				dense
				outlined
				hide-details
				clearable
				class="mr-2"
				style="max-width: 220px;"
				@change="loadKanban"
			></v-select>

			<!-- Text Search Filter -->
			<v-text-field
				v-model="filters.search"
				placeholder="Cerca..."
				dense
				outlined
				hide-details
				clearable
				prepend-inner-icon="mdi-magnify"
				class="mr-2 search-field"
				style="max-width: 260px;"
				@input="debouncedSearch"
				@click:clear="loadKanban"
			></v-text-field>

			<!-- Refresh Button -->
			<v-btn icon @click="loadKanban" :loading="loading">
				<v-icon>mdi-refresh</v-icon>
			</v-btn>
		</v-toolbar>

		<!-- Kanban Board -->
		<div class="kanban-board" v-if="!loading || columns.length">
			<opportunity-column
				v-for="column in columns"
				:key="column.status"
				:ref="'column_' + column.status"
				:column="column"
				:filters="filters"
				@status-changed="handleStatusChange"
				@open-opportunity="openOpportunity"
			/>
		</div>

		<!-- Empty State -->
		<div v-if="!loading && !columns.length" class="empty-state text-center py-12">
			<v-icon size="64" color="grey lighten-1">mdi-clipboard-text-off-outline</v-icon>
			<h3 class="mt-4 grey--text">{{ $trans('no_opportunities_found') }}</h3>
			<p class="grey--text text--lighten-1">Prova a modificare i filtri</p>
		</div>
	</div>
</template>

<script>
import OpportunityColumn from './OpportunityColumn.vue'

export default {
	name: 'OpportunitiesKanban',

	components: {
		OpportunityColumn
	},

	data() {
		return {
			loading: false,
			columns: [],
			filters: {
				period: 3,
				division_id: null,
				owner_id: null,
				search: ''
			},
			searchTimeout: null,
			periodOptions: [
				{ text: 'Ultimi 3 mesi', value: 3 },
				{ text: 'Ultimi 6 mesi', value: 6 },
				{ text: 'Ultimo anno', value: 12 }
			],
			divisions: [],
			owners: []
		}
	},

	created() {
		this.loadFiltersData()
		this.loadKanban()
	},

	beforeDestroy() {
		if (this.searchTimeout) {
			clearTimeout(this.searchTimeout)
		}
	},

	methods: {
		debouncedSearch() {
			if (this.searchTimeout) {
				clearTimeout(this.searchTimeout)
			}
			this.searchTimeout = setTimeout(() => {
				this.loadKanban()
			}, 400)
		},

		async loadFiltersData() {
			try {
				// Load filter options from opportunities-info endpoint
				const infoResponse = await this.axios.get('opportunities-info')
				if (infoResponse.data && infoResponse.data.info) {
					const info = infoResponse.data.info

					// Extract filter options from availableFilters
					if (info.availableFilters) {
						info.availableFilters.forEach(filter => {
							// Division filter
							if (filter.filterset && filter.filterset.name === 'division' && filter.options) {
								this.divisions = filter.options
							}
							// Owner filter
							if (filter.filterset && filter.filterset.name === 'owner' && filter.options) {
								this.owners = filter.options
							}
							// Direct key match
							if (filter.key === 'division_id' && filter.options) {
								this.divisions = filter.options
							}
							if (filter.key === 'owner_id' && filter.options) {
								this.owners = filter.options
							}
						})
					}
				}
			} catch (error) {
				console.error('Error loading filter data:', error)
			}
		},

		async loadKanban() {
			this.loading = true
			try {
				const response = await this.axios.post('opportunities-kanban', this.filters)

				if (response.data && response.data.columns) {
					this.columns = response.data.columns
				}
			} catch (error) {
				this.$eventBusUtility.$emit('errorHandler', error)
			} finally {
				this.loading = false
				// End global progress to show the component
				this.$store.commit('endProgress')
			}
		},

		async handleStatusChange({ opportunityId, newStatus, oldStatus, opportunity }) {
			try {
				const response = await this.axios.put(`opportunities/${opportunityId}/status`, {
					status: newStatus
				})

				if (response.data && response.data.success) {
					// Show success message
					this.$eventBusUtility.setSnackbar({
						data: {
							message: 'Stato aggiornato con successo',
							type: 'success'
						}
					}, this.$store)

					// Update local column counts using oldStatus from event
					this.updateColumnCounts(oldStatus, newStatus)
				}
			} catch (error) {
				this.$eventBusUtility.$emit('errorHandler', error)
				// Reload to restore correct state
				this.loadKanban()
			}
		},

		updateColumnCounts(oldStatus, newStatus) {
			// Decrement old column count
			const oldColumn = this.columns.find(c => c.status === oldStatus)
			if (oldColumn) {
				oldColumn.count = Math.max(0, oldColumn.count - 1)
			}

			// Increment new column count
			const newColumn = this.columns.find(c => c.status === newStatus)
			if (newColumn) {
				newColumn.count += 1
			}
		},

		openOpportunity(opportunityId) {
			this.$router.push({
				path: `/opportunities/${opportunityId}/`
			})
		}
	}
}
</script>

<style scoped>
.opportunities-kanban-wrapper {
	display: block;
}

.opportunities-kanban-wrapper >>> .v-toolbar__content {
	padding: 4px 16px;
}

.kanban-board {
	display: flex;
	overflow-x: auto;
	overflow-y: hidden;
	height: calc(100vh - 169px);
	padding: 8px 16px 16px 16px;
	align-items: flex-start;
}

.search-field >>> .v-input__prepend-inner {
	align-self: center;
	margin-top: 0;
}
</style>
